import { useEffect, useState } from "react";

import { isApiClientError } from "../api/errors";
import type { FileRecord } from "../entities/files";
import { CloseButton } from "./CloseButton";
import { fileTypeLabel } from "./formatting";
import type { ShellServices } from "./services";
import { Notice } from "./Notice";

type Preview =
  | { readonly kind: "image"; readonly url: string }
  | { readonly kind: "pdf"; readonly url: string }
  | { readonly kind: "text"; readonly body: string }
  | { readonly kind: "unsupported" };

/**
 * Форматы, которые показываются исходным текстом.
 *
 * HTML сюда входит намеренно: разметка вложения показывается как текст и
 * никогда не отрисовывается. Отрисовать её значило бы выполнить чужой код в
 * окне ERP.
 */
const TEXT_MIME_TYPES = [
  "text/plain",
  "text/csv",
  "text/markdown",
  "text/html",
  "text/xml",
  "application/xml",
  "application/json",
  // SVG — это разметка, которая умеет выполнять скрипты. Показывается
  // исходным текстом по той же причине, что и HTML.
  "image/svg+xml",
];

/** Больше миллиона знаков в окне предпросмотра не читает никто. */
const TEXT_PREVIEW_LIMIT = 200_000;

function formatJson(body: string): string {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    // Файл с битым JSON всё равно надо показать — как есть.
    return body;
  }
}

// Тип берётся из ответа предпросмотра: по ссылке приходит не обязательно
// исходный файл — офисные документы бэкенд отдаёт сконвертированным PDF.
// Разбирать документы на клиенте больше не нужно, поэтому браузеру остаётся
// показать либо картинку, либо PDF. Тип из карточки файла — запасной вариант
// на случай ответа без mime_type.
function previewKind(mimeType: string): Preview["kind"] {
  const base = mimeType.split(";")[0]?.trim() ?? mimeType;
  if (TEXT_MIME_TYPES.includes(base)) return "text";
  if (base.startsWith("image/")) return "image";
  if (base === "application/pdf") return "pdf";
  return "unsupported";
}

// Офисный документ при первом открытии конвертируется в PDF: сервер отвечает 409,
// пока предпросмотр готовится. Это не ошибка — запрос повторяется, пока файл не
// будет собран, но не бесконечно.
const PREPARING_RETRIES = 8;
const PREPARING_DELAY_MS = 1500;

async function loadWhenReady<T>(
  load: () => Promise<T>,
  isActive: () => boolean,
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await load();
    } catch (error) {
      const preparing = isApiClientError(error) && error.status === 409;
      if (!preparing || attempt >= PREPARING_RETRIES || !isActive())
        throw error;
      await new Promise((resolve) => setTimeout(resolve, PREPARING_DELAY_MS));
    }
  }
}

async function preview(url: string, servedMime: string): Promise<Preview> {
  const kind = previewKind(servedMime);
  if (kind === "unsupported") return { kind };
  if (kind !== "text") return { kind, url };

  // Текст забирается тем же подписанным адресом и показывается разметкой
  // страницы, а не встроенным окном: содержимое файла остаётся данными.
  const response = await fetch(url);
  if (!response.ok) throw new Error("preview request failed");
  const body = (await response.text()).slice(0, TEXT_PREVIEW_LIMIT);
  const base = servedMime.split(";")[0]?.trim() ?? servedMime;

  return { kind, body: base === "application/json" ? formatJson(body) : body };
}

/**
 * Предпросмотр по подписанной ссылке.
 *
 * Источник ссылки задаётся снаружи: у файла хранилища и у версии вложения
 * документа разные адреса, а показывается результат одинаково — картинкой либо
 * PDF. Поведение самого предпросмотра при этом не меняется.
 */
export function SignedPreviewDialog({
  title,
  mimeType,
  loadPreview,
  onClose,
}: {
  title: string;
  mimeType: string;
  loadPreview: () => Promise<{ url: string; mime_type?: string | null }>;
  onClose: () => void;
}) {
  const [content, setContent] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void loadWhenReady(loadPreview, () => active)
      .then((data) => preview(data.url, data.mime_type ?? mimeType))
      .then((content) => {
        if (active) setContent(content);
      })
      .catch(() => {
        if (active)
          setError(
            "Не удалось получить файл для предпросмотра. Повторите попытку или скачайте его.",
          );
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- источник ссылки задаётся вызывающим и не меняется за время показа окна.
  }, [title]);

  return (
    <div
      className="file-preview-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="file-preview"
        role="dialog"
        aria-modal="true"
        aria-labelledby="file-preview-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="file-preview__header">
          <div>
            <p className="eyebrow">Предпросмотр</p>
            <h2 id="file-preview-title">{title}</h2>
            <p>{fileTypeLabel(mimeType)}</p>
          </div>
          <CloseButton label="Закрыть предпросмотр" onClick={onClose} />
        </header>
        <div className="file-preview__content" aria-busy={!content && !error}>
          {error ? <Notice>{error}</Notice> : null}
          {!content && !error ? (
            <p>Подготавливаем безопасный предпросмотр…</p>
          ) : null}
          {content?.kind === "image" ? (
            <img src={content.url} alt={`Предпросмотр: ${title}`} />
          ) : null}
          {content?.kind === "pdf" ? (
            <iframe
              src={content.url}
              referrerPolicy="no-referrer"
              title={`Предпросмотр: ${title}`}
            />
          ) : null}
          {content?.kind === "text" ? (
            <pre className="file-preview__text">{content.body}</pre>
          ) : null}
          {content?.kind === "unsupported" ? (
            <p>
              Предпросмотра для этого формата нет — файл доступен для
              скачивания. В окне показываются изображения, PDF, офисные
              документы Word, Excel, PowerPoint и OpenDocument, а также текст,
              разметка, CSV, XML, Markdown и JSON.
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export function FilePreviewDialog({
  file,
  services,
  onClose,
}: {
  file: FileRecord;
  services: ShellServices;
  onClose: () => void;
}) {
  return (
    <SignedPreviewDialog
      title={file.original_name ?? file.filename}
      mimeType={file.mime_type}
      loadPreview={() =>
        services.files.preview(file.id).then(({ data }) => data)
      }
      onClose={onClose}
    />
  );
}
